import React, { useState } from "react";
import {
  IconButton,
  InputAdornment,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";

// Store Master's schema names things confusingly: `name` is actually the 9-digit Store
// Code and `ownerName` is the real store name — `storeNumber` (5-digit) is the Store Code
// shown/searched here, matching what the Excel "Store Code" column resolves against.
const matchesSearch = (store, search) => {
  const target = search.trim().toLowerCase();
  if (!target) return true;
  return ["storeNumber", "code", "ownerName", "name", "city", "state", "region"].some((field) =>
    (store[field] || "").toString().toLowerCase().includes(target)
  );
};

// Read-only, non-blocking viewer for the stores a single City/State/Region selection
// resolves to. Takes no dependency on which field (city/state/region) produced `stores`,
// so the same component/logic is reused for all three.
const ResolvedStoresViewer = ({ anchorEl, open, onClose, title, stores = [] }) => {
  const [search, setSearch] = useState("");

  const filteredStores = stores.filter((store) => matchesSearch(store, search));

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        setSearch("");
        onClose();
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <div style={{ width: 480, padding: 12 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Typography variant="subtitle2">
            {title} ({stores.length})
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </div>

        <TextField
          size="small"
          fullWidth
          placeholder="Search by code, store name, city, state, region"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          style={{ marginBottom: 8 }}
        />

        <TableContainer style={{ maxHeight: 360, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Store Name</TableCell>
                <TableCell>City</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Region</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No stores found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStores.map((store) => (
                  <TableRow key={store._id || store.id}>
                    <TableCell>{store.storeNumber || store.code || "-"}</TableCell>
                    <TableCell>{store.ownerName || store.name || "-"}</TableCell>
                    <TableCell>{store.city || "-"}</TableCell>
                    <TableCell>{store.state || "-"}</TableCell>
                    <TableCell>{store.region || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Popover>
  );
};

export default ResolvedStoresViewer;
